/**
 * Completion content for the "schedule-intake" activity.
 * Captures scheduled intake date and selected Benchmark practitioner role, propagating
 * handoff data used to update patient assignment state during completion orchestration.
 * Creates a Communication (progress log) to record the scheduling interaction.
 * @agentic connections
 */

import { useForm } from "@mantine/form";
import { Alert, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  Patient,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";
import {
  COMMUNICATION_MEDIUM_SELECT_OPTIONS,
  INTAKE_BILLING_FLAG_SELECT_OPTIONS,
  type IntakeBillingFlagCode,
} from "@benchmark/core/constants";
import { CardSelect, FormField } from "@benchmark/ui/components/atomic";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { PractitionerRoleSearchSelect } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import { useBenchmarkStaffSearch } from "@/hooks/shared/useFHIROptions";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { findBenchmarkPractitionerRole } from "@benchmark/core/services";
import {
  logActivityProgress,
  updatePatientIntakeEligibilityTag,
} from "@benchmark/core/actions";
import { getPatientIntakeEligibility } from "@benchmark/core/resolvers";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import { showWarningNotification } from "@benchmark/ui/utils";
import { CONFIG } from "@/app/config";
import type { ActivityCompletionData } from "@benchmark/core/constants";
import { toDateInputValue } from "@benchmark/ui/utils";
import type { CompletionContentProps } from "./DefaultCompletionContent";

const CONTACT_METHOD_OPTIONS = COMMUNICATION_MEDIUM_SELECT_OPTIONS.filter(
  (o) => o.value === "phone" || o.value === "text",
);

/**
 * Form state for the schedule-intake completion wizard.
 * @agentic connections
 */
type ScheduleIntakeFormData = {
  billingFlag: string;
  scheduledDate: string;
  selectedLcswId: string;
  selectedLcswDisplay: string;
  contactMethod: string;
  notes: string;
};

/**
 * Completion form for schedule-intake: date picker + primary advocate select + contact method + notes.
 * @agentic connections
 */
export const ScheduleIntakeCompletionContent = ({
  task,
  onComplete,
  onCancel,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const patientId = getResourceIdFromReference(task.for?.reference, "Patient");
  const { data: patientResourceRaw } = usePatientResource(patientId);
  const patient = patientResourceRaw as Patient | undefined;
  const [practitionerRole, setPractitionerRole] = useState<
    PractitionerRole | undefined
  >();

  useEffect(() => {
    if (!profile?.id) return;
    findBenchmarkPractitionerRole(medplum, {
      reference: `Practitioner/${profile.id}`,
      benchmarkOrganizationId: CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    })
      .then(setPractitionerRole)
      .catch(() => setPractitionerRole(undefined));
  }, [medplum, profile?.id]);

  const form = useForm<ScheduleIntakeFormData>({
    initialValues: {
      billingFlag: "",
      scheduledDate: "",
      selectedLcswId: "",
      selectedLcswDisplay: "",
      contactMethod: "",
      notes: "",
    },
    validate: {
      billingFlag: (value) =>
        !value ? "Intake eligibility is required" : null,
      scheduledDate: (value) =>
        !value ? "Scheduled intake date is required" : null,
      selectedLcswId: (value) =>
        !value ? "Primary advocate is required" : null,
    },
  });

  // Prefill the eligibility selection from the patient's current tag so
  // reopening the task shows the prior choice (FR-A2.2).
  useEffect(() => {
    if (!patient) return;
    const existing = getPatientIntakeEligibility(patient);
    if (existing && !form.values.billingFlag) {
      form.setFieldValue("billingFlag", existing);
    }
    // Only re-run when the patient resolves; form is a stable ref per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);

  const [lcswSearchQuery, setLcswSearchQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();

  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Details",
        subtitle: "Set intake schedule",
        requiredFields: ["billingFlag", "scheduledDate", "selectedLcswId"],
      },
      {
        id: "assignment",
        title: "Assignment",
        subtitle: "Review next task assignment",
      },
    ],
    [],
  );

  const {
    options: lcswOptions,
    resources: lcswResources,
    isSearching,
  } = useBenchmarkStaffSearch(
    lcswSearchQuery,
    CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
  );

  const handleLcswChange = useCallback(
    (value: string | null) => {
      form.setFieldValue("selectedLcswId", value ?? "");
      if (value) {
        const role = lcswResources.find((r) => r.id === value);
        form.setFieldValue(
          "selectedLcswDisplay",
          role?.practitioner?.display ?? "",
        );
      } else {
        form.setFieldValue("selectedLcswDisplay", "");
      }
    },
    [form, lcswResources],
  );

  const handleSubmit = useCallback(
    async (values: ScheduleIntakeFormData) => {
      if (
        !values.billingFlag ||
        !values.scheduledDate ||
        !values.selectedLcswId
      )
        return;

      setSubmitting(true);
      setError(null);

      try {
        // Persist the intake billing ELIGIBILITY first as a patient-level meta
        // tag — the advisory source the unified intake reads at complete-intake
        // to prefill the clinician's billing reality. Failure is blocking: the
        // activity must not advance without a persisted eligibility.
        if (!patient) {
          throw new Error(
            "Cannot record the intake billing eligibility (missing patient). Please refresh and try again.",
          );
        }
        await updatePatientIntakeEligibilityTag(
          medplum,
          patient,
          values.billingFlag as IntakeBillingFlagCode,
        );

        if (task.id && patient && profile && practitionerRole) {
          try {
            await logActivityProgress(medplum, {
              task,
              progressData: {
                contactMethod:
                  (values.contactMethod as "phone" | "text") || undefined,
                outcome: "general-progress",
                outcomeDisplay: "Intake Scheduled",
                notes:
                  values.notes ||
                  `Intake scheduled for ${new Date(values.scheduledDate).toLocaleString()} with ${values.selectedLcswDisplay}`,
                snoozeBehavior: { type: "none" },
              },
              patient,
              practitioner: profile,
              practitionerRole,
            });
          } catch {
            showWarningNotification(
              "Scheduling interaction could not be logged, but completion will proceed.",
            );
          }
        }

        const dueDateIso = new Date(values.scheduledDate).toISOString();

        const completionData: ActivityCompletionData = {
          nextActivityDueDate: dueDateIso,
          patientBenchmarkPractitionerRoleRef: values.selectedLcswId,
          patientBenchmarkPractitionerRoleDisplay: values.selectedLcswDisplay,
          statusReason: values.notes || undefined,
        };

        await onComplete(values.notes || undefined, completionData);
        await teeForTask({
          task,
          minutes: careMinutes,
          method: "task-completion",
          notes: values.notes || undefined,
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to complete task",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      onComplete,
      task,
      patient,
      profile,
      practitionerRole,
      medplum,
      teeForTask,
      careMinutes,
    ],
  );

  useEffect(() => {
    onDirtyChange?.(
      !!form.values.billingFlag ||
        !!form.values.scheduledDate ||
        !!form.values.selectedLcswId ||
        !!form.values.contactMethod ||
        form.values.notes.trim().length > 0 ||
        !!nextTaskOwnerOverride ||
        !!careMinutes,
    );
  }, [form.values, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  const today = toDateInputValue(new Date());

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={handleSubmit}
        isSubmitting={submitting}
        submitText="Complete & Advance to Intake"
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <Text size="sm">
              Confirm the scheduled intake date and assign the primary advocate
              who will conduct the intake.
            </Text>

            <FormField
              label="Eligibility"
              required
              error={
                typeof form.errors.billingFlag === "string"
                  ? form.errors.billingFlag
                  : undefined
              }
            >
              <CardSelect
                data={INTAKE_BILLING_FLAG_SELECT_OPTIONS}
                value={form.values.billingFlag || null}
                onChange={(value) => {
                  const singleValue = Array.isArray(value) ? value[0] : value;
                  form.setFieldValue("billingFlag", singleValue ?? "");
                }}
                columns={3}
                compact
                error={Boolean(form.errors.billingFlag)}
                aria-label="Select eligibility"
              />
            </FormField>

            <TextInput
              type="datetime-local"
              label="Scheduled Intake Date & Time"
              required
              value={form.values.scheduledDate}
              onChange={(e) =>
                form.setFieldValue("scheduledDate", e.currentTarget.value)
              }
              error={form.errors.scheduledDate}
              min={`${today}T00:00`}
            />

            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Primary Advocate <span aria-hidden="true">*</span>
              </Text>
              <PractitionerRoleSearchSelect
                options={lcswOptions}
                value={form.values.selectedLcswId || null}
                searchQuery={lcswSearchQuery}
                isSearching={isSearching}
                onChange={handleLcswChange}
                onSearchChange={setLcswSearchQuery}
                placeholder="Search staff..."
                noResultsMessage="No matching staff found"
                error={form.errors.selectedLcswId}
              />
            </Stack>

            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Contact Method
              </Text>
              <CardSelect
                data={CONTACT_METHOD_OPTIONS}
                value={form.values.contactMethod || null}
                onChange={(value) => {
                  const singleValue = Array.isArray(value) ? value[0] : value;
                  form.setFieldValue("contactMethod", singleValue ?? "");
                }}
                columns={2}
                compact
                aria-label="Select contact method"
              />
            </Stack>

            <Textarea
              label="Notes (optional)"
              placeholder="Add scheduling notes..."
              {...form.getInputProps("notes")}
              autosize
              minRows={2}
              maxRows={4}
            />
          </Stack>
        </FormStep>

        <FormStep id="assignment">
          <Stack gap="md">
            <NextTaskOwnerSection
              nextActivityLabel={nextActivityLabel}
              defaultOwner={nextActivityDefaultOwner}
              appliedRule={nextActivityAppliedRule}
              overrideOwner={nextTaskOwnerOverride}
              onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
              isResolving={isResolvingNextTaskOwner}
            />
            <CareTimeActivityDurationField
              minutes={careMinutes}
              onMinutesChange={setCareMinutes}
            />
          </Stack>
        </FormStep>
      </SteppedForm>
    </Stack>
  );
};
